import { produce } from 'immer'

import { ShareListItemFragment, NestedProjectFragment } from '@sketch/gql-types'
import { ShareSortOrder } from '../../hooks/useSortSettings'

export function getSortFunction(
  sortOrder: ShareSortOrder | 'ARCHIVED_AT_DESC'
) {
  return (a: ShareListItemFragment, b: ShareListItemFragment) => {
    // Prioritize shares that are pinned by the current user
    if (a.pinnedByCurrentUserAt && !b.pinnedByCurrentUserAt) {
      return -1
    }
    if (!a.pinnedByCurrentUserAt && b.pinnedByCurrentUserAt) {
      return 1
    }
    if (a.pinnedByCurrentUserAt && b.pinnedByCurrentUserAt) {
      const aTime = new Date(a.pinnedByCurrentUserAt).getTime()
      const bTime = new Date(b.pinnedByCurrentUserAt).getTime()

      // Extract the suffix from the sortOrder
      const sortOrderSuffix = sortOrder.split('_').pop()

      if (sortOrderSuffix === 'ASC') {
        return aTime - bTime
      } else if (sortOrderSuffix === 'DESC') {
        return bTime - aTime
      }
    }

    // If neither or both are pinned, sort based on the sortOrder
    switch (sortOrder) {
      case 'CREATED_AT_DESC': {
        if (!b.createdAt || !a.createdAt) return 0

        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }
      case 'CREATED_AT_ASC': {
        if (!b.createdAt || !a.createdAt) return 0

        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      }
      case 'LAST_MODIFIED_ASC': {
        if (!a?.version?.createdAt || !b?.version?.createdAt) return 0

        return (
          new Date(a.version.createdAt).getTime() -
          new Date(b.version.createdAt).getTime()
        )
      }
      case 'ARCHIVED_AT_DESC': {
        if (!a?.archivedAt || !b?.archivedAt) return 0

        return (
          new Date(b.archivedAt).getTime() - new Date(a.archivedAt).getTime()
        )
      }
      case 'LAST_MODIFIED_DESC': {
        if (!a?.version?.createdAt || !b?.version?.createdAt) return 0

        return (
          new Date(b.version.createdAt).getTime() -
          new Date(a.version.createdAt).getTime()
        )
      }
      case 'NAME_ASC':
        return a.name
          .replace(/^\W+/, '')
          .localeCompare(b.name.replace(/^\W+/, ''))
      case 'NAME_DESC':
        return b.name
          .replace(/^\W+/, '')
          .localeCompare(a.name.replace(/^\W+/, ''))
      default:
        return 0
    }
  }
}

const isShare = (
  x: ShareListItemFragment | NestedProjectFragment
): x is ShareListItemFragment => x.__typename === 'Share'

const isNestedProject = (
  x: ShareListItemFragment | NestedProjectFragment
): x is NestedProjectFragment => x.__typename === 'Project'

type StorageItem = ShareListItemFragment | NestedProjectFragment

interface UpdatePinnedSharesProps<T> {
  readData: () => T | undefined
  writeData: (newData: T) => void
  pickPagination: (data: T) => { entries: StorageItem[] } | undefined
  transformEntries?: (entries: StorageItem[]) => StorageItem[] // optional transformation, so that we could perform additional sorting for specific cases (E.g. storage items)
  shareId: string
  sort?: ShareSortOrder | 'ARCHIVED_AT_DESC'
}

export function updatePinnedShares<T>({
  pickPagination,
  transformEntries,
  readData,
  writeData,
  shareId,
  sort,
}: UpdatePinnedSharesProps<T>) {
  const existingData = readData()

  if (!existingData) return
  const currentPagination = pickPagination(existingData)
  if (!currentPagination) return

  const shares = currentPagination.entries.filter(isShare) || []
  const projects = currentPagination.entries.filter(isNestedProject) || []
  const pinnedShare = shares.find(entry => entry.identifier === shareId)

  // `pinnedShare` is already updated on cache after the mutation, `justPinned`
  //  will be true if the share was pinned, false if it was unpinned
  const justPinned = Boolean(pinnedShare?.pinnedByCurrentUserAt)

  if (!existingData || !pinnedShare) return

  // The new state will sort the items by the pinnedByCurrentUserAt field
  // The newer pinned items will be on the top
  const newState = produce(existingData, draftState => {
    const currentPagination = pickPagination(existingData)
    if (!currentPagination) return

    if (justPinned) {
      const existingSharesWithoutPinned =
        shares.filter(entry => entry.identifier !== shareId) || []

      const newPagination = pickPagination(draftState as T)
      if (!newPagination) return

      newPagination.entries = [
        ...projects,
        pinnedShare,
        ...existingSharesWithoutPinned,
      ]

      if (transformEntries) {
        newPagination.entries = transformEntries(newPagination.entries)
      }
    } else {
      if (!sort) return

      const newPagination = pickPagination(draftState as T)
      if (!newPagination) return

      const newSharesAfterUnpinning = shares.sort(getSortFunction(sort))

      newPagination.entries = [...projects, ...newSharesAfterUnpinning]

      if (transformEntries) {
        newPagination.entries = transformEntries(currentPagination.entries)
      }
    }
  })

  writeData(newState)
}
